import { useState } from "react";
import { Lock, LockOpen, Pencil, Check, X } from "lucide-react";
import { CATEGORY_META, PROVENANCE } from "@/lib/vocab";
import { cn } from "@/lib/utils";

const CATS = ["identity", "audience", "positioning", "voice", "proof", "guardrails", "visual"];

function valueToText(v) {
  if (v == null) return "";
  if (Array.isArray(v)) return v.map((x) => (typeof x === "object" ? `${x.name || ""} ${x.hex || ""} ${x.usage || ""}`.trim() : x)).join("\n");
  if (typeof v === "object") return Object.entries(v).map(([k, val]) => `${k}: ${val}`).join("\n");
  return String(v);
}

function Meter({ ratio, missing }) {
  const pct = Math.round((ratio || 0) * 100);
  const r = 34, c = 2 * Math.PI * r;
  return (
    <div className="flex items-center gap-4" data-testid="dna-meter">
      <div className="relative h-[84px] w-[84px] shrink-0">
        <svg width="84" height="84" viewBox="0 0 84 84" aria-label={`${pct}% complete`}>
          <circle cx="42" cy="42" r={r} fill="none" stroke="hsl(var(--border))" strokeWidth="4" />
          <circle cx="42" cy="42" r={r} fill="none" stroke="#CCB686" strokeWidth="4" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)} transform="rotate(-90 42 42)" style={{ transition: "stroke-dashoffset 1.2s cubic-bezier(.16,1,.3,1)" }} />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center font-display text-lg" data-testid="dna-meter-pct">{pct}%</span>
      </div>
      <div>
        <div className="font-display text-lg leading-tight">Your DNA</div>
        <div className="text-xs text-muted-foreground mt-1" data-testid="dna-missing-count">{missing?.length ? `${missing.length} of 26 fields still open` : "Every field captured"}</div>
        {pct >= 80 && <div className="text-xs gold-text mt-1">Ready to reveal</div>}
      </div>
    </div>
  );
}

function FieldRow({ meta, value, prov, locked, onSave, onLock }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const filled = Array.isArray(value) ? value.length > 0 : typeof value === "object" && value ? Object.keys(value).length > 0 : !!(value && String(value).trim());
  const p = PROVENANCE[prov?.source];
  const start = () => { setDraft(valueToText(value)); setEditing(true); };
  const save = async () => { await onSave(meta.field, meta.type === "list" ? draft : draft); setEditing(false); };
  const canEdit = meta.type === "text" || meta.type === "list";
  return (
    <div className={cn("rounded-xl border px-3 py-2.5 transition-all duration-500", filled ? (prov?.source === "ai_derived" ? "border-gold/50 glow-gold" : "border-border bg-background/40") : "border-dashed border-border/70")} data-testid={`dna-field-${meta.field}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", filled ? "bg-gold" : "bg-border")} />
            <span className={cn("text-xs font-medium", !filled && "text-muted-foreground")}>{meta.label}</span>
          </div>
          {p && filled && <span className={cn("ml-3.5 mt-0.5 inline-block text-[10px] font-mono-ls", p.tone === "gold" ? "gold-text" : "text-muted-foreground")} data-testid={`dna-prov-${meta.field}`}>{p.label}</span>}
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          {canEdit && !editing && <button type="button" onClick={start} aria-label={`Edit ${meta.label}`} data-testid={`dna-edit-${meta.field}`} className="btn-quiet h-7 w-7 px-0" disabled={locked}><Pencil className="h-3 w-3" /></button>}
          <button type="button" onClick={() => onLock(meta.field, !locked)} aria-label={locked ? "Unlock" : "Lock"} data-testid={`dna-lock-${meta.field}`} className={cn("btn-quiet h-7 w-7 px-0", locked && "text-gold")}>{locked ? <Lock className="h-3 w-3" /> : <LockOpen className="h-3 w-3" />}</button>
        </div>
      </div>
      {editing ? (
        <div className="mt-2 space-y-2">
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={meta.type === "list" ? 4 : 3} data-testid={`dna-edit-input-${meta.field}`} className="w-full text-sm rounded-lg border border-border bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gold/60" placeholder={meta.type === "list" ? "One item per line" : meta.guidance} />
          <div className="flex gap-2">
            <button type="button" onClick={save} className="btn-gold h-8 px-3 text-xs" data-testid={`dna-edit-save-${meta.field}`}><Check className="h-3 w-3" />Save</button>
            <button type="button" onClick={() => setEditing(false)} className="btn-quiet h-8 px-3 text-xs"><X className="h-3 w-3" />Cancel</button>
          </div>
        </div>
      ) : filled ? (
        <p className="mt-1.5 ml-3.5 text-xs text-foreground/80 whitespace-pre-wrap line-clamp-3" data-testid={`dna-value-${meta.field}`}>{valueToText(value)}</p>
      ) : (
        <p className="mt-1 ml-3.5 text-[11px] text-muted-foreground/70">{meta.guidance}</p>
      )}
    </div>
  );
}

export function DnaPanel({ dna, fields, completeness, onSave, onLock, className }) {
  if (!dna || !fields) return null;
  const locked = new Set(dna.locked_fields || []);
  return (
    <div className={cn("space-y-5", className)} data-testid="dna-panel">
      <Meter ratio={completeness?.ratio} missing={completeness?.missing} />
      <div className="space-y-3">
        {CATS.map((cat) => {
          const catFields = fields.filter((f) => f.category === cat);
          const filledCount = catFields.filter((f) => completeness?.filled?.includes(f.field)).length;
          const full = filledCount === catFields.length;
          return (
            <section key={cat} className={cn("rounded-2xl border p-3 transition-colors duration-700 surface", full ? "border-gold/50" : "border-border")} data-testid={`dna-category-${cat}`}>
              <header className="flex items-center justify-between px-1 mb-2">
                <div>
                  <div className="font-display text-base">{CATEGORY_META[cat].label}</div>
                  <div className="text-[11px] text-muted-foreground">{CATEGORY_META[cat].blurb}</div>
                </div>
                <span className={cn("font-mono-ls text-[11px]", full ? "gold-text" : "text-muted-foreground")}>{filledCount}/{catFields.length}</span>
              </header>
              <div className="space-y-2">
                {catFields.map((f) => <FieldRow key={f.field} meta={f} value={dna[f.field]} prov={dna.field_provenance?.[f.field]} locked={locked.has(f.field)} onSave={onSave} onLock={onLock} />)}
              </div>
            </section>
          );
        })}
      </div>
      {completeness?.missing?.length > 0 && (
        <div className="text-xs text-muted-foreground" data-testid="dna-missing-list">
          <span className="eyebrow-label block mb-1">Still open</span>
          {completeness.missing_labels.join(" · ")}
        </div>
      )}
    </div>
  );
}
